import { StoreOrderItem } from '@/api/store-orders-types'
import { StoreProductVariant } from '@/api/store-products-types'
import { useUpdateProductQuantity } from '@/api/api-mutations'
import { preloadGetProducts as preloadGetProducts, useGetProducts } from '@/api/react-query'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { SearchInput } from '@/components/search-input'
import { orderIdSubject, useGetCurrentOrder } from '@/contexts/order-context'
import { useI18n } from '@/i18n/i18n-react'
import { useGetSubject } from '@/utils/hooks'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { Minus, Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import { z } from 'zod'
import { Image } from '@/components/ui/image'
import { useMemo } from 'react'
import { ErrorDisplay } from '@/components/error-display'
import { tenant } from '@/tenants/tenant'
import { resolveHomeRedirect } from '@/utils/route-guards'

export const Route = createFileRoute('/')({
	component: ProductList,
	beforeLoad: () => {
		const target = resolveHomeRedirect(tenant.features)
		if (target) {
			throw redirect({ to: target })
		}
	},
	validateSearch: z.object({
		category: z.string().optional(),
		q: z.string().optional(),
		limit: z.number().optional(),
		offset: z.number().optional()
	}),
	loaderDeps: ({ search: { category, q, limit, offset } }) => ({
		category,
		q,
		limit,
		offset
	}),
	loader: ({ context, deps: { category, q, limit, offset } }) =>
		preloadGetProducts(context, {
			query: { categoryUuid: category, q, limit: limit?.toString(), offset: offset?.toString() }
		})
})

const ProductCard = ({
	product,
	orderItem
}: {
	product: StoreProductVariant
	orderItem: StoreOrderItem | undefined
}) => {
	const { L } = useI18n()
	const updateProductQuantity = useUpdateProductQuantity()
	const orderUuid = useGetSubject(orderIdSubject)
	const navigate = Route.useNavigate()

	return (
		<div
			onClick={() => {
				navigate({
					to: '/product/$productId',
					params: { productId: product.uuid }
				})
			}}
			className='block cursor-pointer transition-transform hover:-translate-y-1'
		>
			<Card className='h-full overflow-hidden border-0 shadow-md transition-shadow hover:shadow-xl'>
				<div className='flex h-full flex-col p-0'>
					<div className='group relative'>
						<div className='w-full bg-gray-50'>
							<Image
								src={product.logoUrl}
								alt={product.title}
								className='h-40 w-full object-contain p-2 sm:h-56 sm:p-4 lg:h-64'
							/>
						</div>
						<div className='absolute inset-0 bg-black/5 opacity-0 transition-opacity group-hover:opacity-100' />
					</div>
					<div className='flex flex-1 flex-col space-y-2 p-3 sm:space-y-3 sm:p-4 lg:space-y-4 lg:p-6'>
						<div>
							<h3 className='text-foreground line-clamp-1 text-sm font-medium sm:text-lg lg:text-xl'>
								{product.title}
							</h3>
							<p className='text-primary mt-1 text-lg font-bold sm:mt-2 sm:text-xl lg:text-2xl'>
								{L.Amount.Default({
									currencyCode: product.currencyCode,
									amount: product.unitPrice?.gross
								})}
							</p>
						</div>
						<div className='flex-grow'>
							<p className='text-muted-foreground line-clamp-2 text-xs sm:text-sm'>
								{product.description}
							</p>
						</div>
						<div className='mt-auto pt-2 sm:pt-3 lg:pt-4'>
							{
								// isOutOfStock ?
								// 	<div className='flex items-center justify-center rounded-lg bg-destructive/10 py-2'>
								// 		<p className='text-sm font-medium text-destructive'>Out of stock</p>
								// 	</div>
								// :
								orderItem ?
									<div className='flex items-center justify-end space-x-2 sm:space-x-3'>
										<Button
											size='icon'
											variant='destructive'
											className='h-8 w-8 shadow-sm transition-shadow hover:shadow-md sm:h-9 sm:w-9'
											onClick={e => {
												e.preventDefault()
												e.stopPropagation()
												updateProductQuantity.mutateAsync({
													path: {
														orderUuid,
														variantUuid: product.uuid
													},
													body: {
														removeFromOrder: true
													}
												})
											}}
										>
											<Trash2 className='h-4 w-4' />
										</Button>
										<div className='bg-secondary/10 flex items-center space-x-1 rounded-lg px-1 py-1 sm:space-x-2 sm:px-2'>
											<Button
												size='icon'
												variant='ghost'
												className='hover:bg-secondary/20 h-6 w-6 sm:h-8 sm:w-8'
												onClick={e => {
													e.preventDefault()
													e.stopPropagation()
													updateProductQuantity.mutateAsync({
														path: {
															orderUuid,
															variantUuid: product.uuid
														},
														body: {
															quantityChange: -1
														}
													})
												}}
											>
												<Minus className='h-4 w-4' />
											</Button>
											<span className='w-6 text-center text-xs font-medium sm:w-8 sm:text-sm'>
												{orderItem.quantityInfo.quantity}
											</span>
											<Button
												size='icon'
												variant='ghost'
												className='hover:bg-secondary/20 h-6 w-6 sm:h-8 sm:w-8'
												onClick={e => {
													e.preventDefault()
													e.stopPropagation()
													updateProductQuantity.mutateAsync({
														path: {
															orderUuid,
															variantUuid: product.uuid
														},
														body: {
															quantityChange: 1
														}
													})
												}}
											>
												<Plus className='h-4 w-4' />
											</Button>
										</div>
									</div>
								:	<Button
										className='w-full text-xs shadow-sm transition-shadow hover:shadow-md sm:text-sm'
										size='sm'
										onClick={e => {
											e.preventDefault()
											e.stopPropagation()
											updateProductQuantity.mutateAsync({
												path: {
													orderUuid,
													variantUuid: product.uuid
												},
												body: {
													quantityChange: 1
												}
											})
										}}
									>
										{L.Product.Details.AddToCart()}
									</Button>

							}
						</div>
					</div>
				</div>
			</Card>
		</div>
	)
}

const PAGE_SIZE = 20
function ProductList() {
	const { L } = useI18n()
	const navigate = Route.useNavigate()
	const { category: categoryUuid, q: searchQuery, limit = PAGE_SIZE, offset } = Route.useSearch()

	// Pagination settings
	const currentOffset = offset || 0
	const currentPage = Math.floor(currentOffset / limit) + 1

	const listProducts = useGetProducts({
		categoryUuid,
		q: searchQuery,
		limit: (limit ?? PAGE_SIZE).toString(),
		offset: (offset ?? 0).toString()
	})
	const activeOrder = useGetCurrentOrder()

	const products = listProducts.data?.items ?? []
	const totalCount = listProducts.data?.count ?? 0
	const totalPages = Math.ceil(totalCount / limit)
	const hasNextPage = currentPage < totalPages
	const hasPrevPage = currentPage > 1

	const paginationInfo = useMemo(() => {
		const start = currentOffset + 1
		const end = Math.min(currentOffset + limit, totalCount)
		return { start, end, total: totalCount }
	}, [currentOffset, limit, totalCount])

	const handleSearchChange = (value: string) => {
		navigate({
			search: { category: categoryUuid, q: value ? value : undefined, limit: undefined, offset: undefined }
		})
	}

	return (
		<div className='container mx-auto max-w-6xl p-4 sm:p-6'>
			{/* Search Input */}
			<div className='mb-6 flex items-center justify-center'>
				<SearchInput
					value={searchQuery || ''}
					onChange={handleSearchChange}
					placeholder={L.Product.Catalog.SearchPlaceholder()}
					className='w-full max-w-md'
				/>
			</div>

			{listProducts.isLoading && (
				<div className='flex items-center justify-center py-12'>
					<LoadingSpinner />
				</div>
			)}

			{listProducts.error && <ErrorDisplay refetch={listProducts.refetch} className='py-6' />}

			{!listProducts.isLoading && !listProducts.error && products.length === 0 && (
				<div className='py-12 text-center'>
					<p className='text-muted-foreground text-lg'>
						{searchQuery ?
							L.Product.Catalog.NoResultsForQuery({ query: searchQuery })
						:	L.Product.Catalog.NoResults()}
					</p>
				</div>
			)}

			{!listProducts.error && products.length > 0 && (
				<>
					{/* Product Grid */}
					<div className='grid grid-cols-2 gap-3 px-1 sm:grid-cols-2 sm:gap-4 sm:px-2 md:grid-cols-2 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4'>
						{products.map(product => (
							<ProductCard
								key={product.uuid}
								product={product}
								orderItem={activeOrder?.data?.itemGroups
									.flatMap(x => x.items)
									.find(x => x.variantUuid === product.uuid)}
							/>
						))}
					</div>

					<div className='mt-8 flex flex-col items-center justify-between gap-4 sm:flex-row'>
						{/* Results info */}
						<div className='text-muted-foreground text-sm'>
							{L.Pagination.Showing({
								start: paginationInfo.start,
								end: paginationInfo.end,
								total: paginationInfo.total
							})}
						</div>

						{/* Pagination controls */}
						<div className='flex items-center gap-2'>
							<Link
								to={`/`}
								search={{
									category: categoryUuid,
									q: searchQuery,
									limit,
									offset: (currentPage - 2) * limit
								}}
								disabled={!hasPrevPage}
							>
								<Button
									variant='outline'
									size='sm'
									disabled={!hasPrevPage}
									className='flex items-center gap-1'
								>
									<ChevronLeft className='h-4 w-4' />
									{L.Pagination.Previous()}
								</Button>
							</Link>

							{/* Page numbers */}
							<div className='flex items-center gap-1'>
								{Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
									const pageNumber = currentPage <= 3 ? i + 1 : currentPage - 2 + i
									if (pageNumber > totalPages) return null

									return (
										<Link
											key={pageNumber}
											to={`/`}
											search={{
												category: categoryUuid,
												q: searchQuery,
												limit,
												offset: (pageNumber - 1) * limit
											}}
										>
											<Button
												variant={pageNumber === currentPage ? 'default' : 'outline'}
												size='sm'
												className='h-8 w-8 p-0'
											>
												{pageNumber}
											</Button>
										</Link>
									)
								})}
							</div>

							<Link
								disabled={!hasNextPage}
								to={`/`}
								search={{
									category: categoryUuid,
									q: searchQuery,
									limit,
									offset: currentPage * limit
								}}
							>
								<Button
									variant='outline'
									size='sm'
									disabled={!hasNextPage}
									className='flex items-center gap-1'
								>
									{L.Pagination.Next()}
									<ChevronRight className='h-4 w-4' />
								</Button>
							</Link>
						</div>
					</div>
				</>
			)}
		</div>
	)
}

export default ProductList
